import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "./adminApi";
import { ConfirmDeleteModal, Field, EmptyState } from "./PapersPanel";
import { Loader2, Plus, Pencil, Trash2, Save, X, Calendar, MapPin } from "lucide-react";

const EMPTY_EVENT = {
  title: "",
  date: new Date().toISOString().slice(0, 10),
  timeRange: "",
  type: "Symposium",
  seriesCode: "EAS",
  speaker: "",
  speakerAffiliation: "",
  speakerBio: "",
  moderator: "",
  location: "",
  format: "",
  registerUrl: "",
  abstract: "",
  tags: [],
};

const EventsPanel = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/content/events");
      setEvents(res.data?.events || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load events.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSave = async () => {
    if (!editing.title.trim() || !editing.date) {
      setError("Title and date are required.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...editing,
        tags: Array.isArray(editing.tags)
          ? editing.tags
          : String(editing.tags || "")
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean),
      };
      if (editing.id) {
        await adminApi.put(`/admin/content/events/${editing.id}`, payload);
      } else {
        await adminApi.post("/admin/content/events", payload);
      }
      setEditing(null);
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save event.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      await adminApi.delete(`/admin/content/events/${confirmDelete.id}`);
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-events-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light text-gray-900 tracking-tight">Events</h2>
          <p className="text-sm text-gray-500 font-light mt-1">
            Manage symposia, dialogues, and open lab days.
          </p>
        </div>
        <button
          onClick={() => {
            setError("");
            setEditing({ ...EMPTY_EVENT });
          }}
          className="inline-flex items-center gap-1.5 text-sm bg-gray-900 hover:bg-black text-white px-4 py-2.5 rounded-md transition-colors"
          data-testid="admin-add-event"
        >
          <Plus className="w-4 h-4" />
          Add event
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : events.length === 0 ? (
        <EmptyState message="No events yet. Add your first event." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {events.map((e, i) => (
            <div
              key={e.id}
              className={`px-5 py-4 flex items-start gap-4 hover:bg-gray-50 transition-colors ${
                i > 0 ? "border-t border-gray-100" : ""
              }`}
            >
              <div className="flex-shrink-0 w-16 text-center">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">
                  {new Date(e.date).toLocaleString("en-US", { month: "short" }).toUpperCase()}
                </div>
                <div className="text-2xl font-light text-gray-900 leading-none mt-1">
                  {new Date(e.date).getDate()}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400 mt-1">
                  {new Date(e.date).getFullYear()}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {e.type && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-gray-500">
                      {e.type}
                    </span>
                  )}
                  {e.seriesCode && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm bg-gray-900 text-white">
                      {e.seriesCode}
                    </span>
                  )}
                </div>
                <div className="text-sm font-medium text-gray-900 leading-snug">{e.title}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-600">
                  {e.timeRange && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {e.timeRange}
                    </span>
                  )}
                  {e.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {e.location}
                    </span>
                  )}
                  {e.speaker && <span className="text-gray-500">· {e.speaker}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => setEditing({ ...e })}
                  className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                  data-testid={`edit-event-${e.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setConfirmDelete(e)}
                  disabled={deletingId === e.id}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                  data-testid={`delete-event-${e.id}`}
                >
                  {deletingId === e.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EventEditModal
          evt={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
          error={error}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          label="event"
          preview={confirmDelete.title}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={handleDelete}
          loading={deletingId === confirmDelete.id}
        />
      )}
    </div>
  );
};

const EventEditModal = ({ evt, onChange, onCancel, onSave, saving, error }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    onClick={onCancel}
  >
    <div
      className="relative w-full max-w-3xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative bg-gray-900 px-6 py-5 flex-shrink-0">
        <button
          onClick={onCancel}
          className="absolute right-4 top-4 text-white/70 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60 mb-2">
          {evt.id ? "Edit event" : "New event"}
        </div>
        <h2 className="text-2xl font-light text-white tracking-tight">Event details</h2>
      </div>

      <div className="p-6 sm:p-7 overflow-y-auto space-y-4">
        <Field label="Title" required value={evt.title} onChange={(v) => onChange({ ...evt, title: v })} testid="event-title" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Date" required type="date" value={evt.date} onChange={(v) => onChange({ ...evt, date: v })} testid="event-date" />
          <Field label="Time range" value={evt.timeRange || ""} onChange={(v) => onChange({ ...evt, timeRange: v })} placeholder="16:00 – 17:30" testid="event-time" />
          <div>
            <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-600 block mb-1.5">Type</label>
            <select
              value={evt.type || ""}
              onChange={(e) => onChange({ ...evt, type: e.target.value })}
              className="w-full px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 font-light"
            >
              <option>Symposium</option>
              <option>Dialogue</option>
              <option>Workshop</option>
              <option>Open Day</option>
              <option>Seminar</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-600 block mb-1.5">Series code</label>
            <select
              value={evt.seriesCode || ""}
              onChange={(e) => onChange({ ...evt, seriesCode: e.target.value })}
              className="w-full px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 font-light"
            >
              <option value="">—</option>
              <option value="EAS">EAS · Emotion AI Symposium</option>
              <option value="CDX">CDX · Cross-Disciplinary Dialogue</option>
              <option value="OLD">OLD · Open Lab Day</option>
            </select>
          </div>
          <Field label="Format" value={evt.format || ""} onChange={(v) => onChange({ ...evt, format: v })} placeholder="Hybrid · Zoom + In-person" testid="event-format" />
        </div>
        <Field label="Speaker" value={evt.speaker || ""} onChange={(v) => onChange({ ...evt, speaker: v })} testid="event-speaker" />
        <Field label="Speaker affiliation" value={evt.speakerAffiliation || ""} onChange={(v) => onChange({ ...evt, speakerAffiliation: v })} testid="event-speaker-aff" />
        <div>
          <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-600 block mb-1.5">Speaker bio</label>
          <textarea
            value={evt.speakerBio || ""}
            onChange={(e) => onChange({ ...evt, speakerBio: e.target.value })}
            rows={3}
            className="w-full px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 font-light resize-none"
          />
        </div>
        <Field label="Moderator" value={evt.moderator || ""} onChange={(v) => onChange({ ...evt, moderator: v })} testid="event-moderator" />
        <Field label="Location" value={evt.location || ""} onChange={(v) => onChange({ ...evt, location: v })} testid="event-location" />
        <Field label="Register URL" value={evt.registerUrl || ""} onChange={(v) => onChange({ ...evt, registerUrl: v })} testid="event-register-url" />
        <div>
          <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-600 block mb-1.5">Abstract</label>
          <textarea
            value={evt.abstract || ""}
            onChange={(e) => onChange({ ...evt, abstract: e.target.value })}
            rows={4}
            className="w-full px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 font-light resize-none"
          />
        </div>
        <Field
          label="Tags (comma-separated)"
          value={Array.isArray(evt.tags) ? evt.tags.join(", ") : evt.tags || ""}
          onChange={(v) => onChange({ ...evt, tags: v })}
          placeholder="fNIRS, Affective Computing"
          testid="event-tags"
        />

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}
      </div>

      <div className="px-6 sm:px-7 py-4 border-t border-gray-100 flex items-center justify-end gap-2 flex-shrink-0">
        <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-900 px-3 py-2">Cancel</button>
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-gray-900 hover:bg-black text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors disabled:opacity-60"
          data-testid="save-event"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
      </div>
    </div>
  </div>
);

export default EventsPanel;
